import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Loader2, Download, Mail, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useToast } from '../ui/use-toast';
import { cn } from '../../lib/utils';
import { getNotificationPermission } from '../../services/pushNotification';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { PushUnblockSteps } from '../push/PushUnblockSteps';
import {
  gymNotificationPreferencesService,
  type GymNotificationPreference,
} from '../../services/gymNotificationPreferences';

export interface NotificationBellProps {
  notifiableType: 'gym';
  notifiableSlug: string;
  visible?: boolean;
  disabled?: boolean;
  /** Extra classes for the trigger button (e.g. to style it as a cover overlay) */
  triggerClassName?: string;
  onSubscriptionChange?: (hasAnyEnabled: boolean) => void;
}

const GYM_TYPES: GymNotificationPreference['notification_type'][] = ['new_climb', 'first_top', 'new_announcement'];

export function NotificationBell({
  notifiableSlug,
  visible = true,
  disabled = false,
  triggerClassName,
  onSubscriptionChange,
}: NotificationBellProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState<GymNotificationPreference[]>([]);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [showUnblockSteps, setShowUnblockSteps] = useState(false);

  // The same push state as every other switch in the app (utils/push-state):
  // turning it on here takes the dashboard's ask away, and the other way round.
  const push = usePushNotifications();
  const { permission } = push;
  const loading = push.isLoading || preferencesLoading;

  const hasAnyPushEnabled = preferences.some((p) => p.push_enabled);
  const canShowDropdown = permission === 'granted' && push.isEnabled;

  const loadPreferences = useCallback(async () => {
    if (!notifiableSlug) return;
    try {
      const prefs = await gymNotificationPreferencesService.getPreferences(notifiableSlug);
      setPreferences(prefs);
      onSubscriptionChange?.(prefs.some((p) => p.push_enabled));
    } catch (error) {
      console.error('Failed to load gym notification preferences:', error);
    }
  }, [notifiableSlug, onSubscriptionChange]);

  useEffect(() => {
    if (canShowDropdown) loadPreferences();
  }, [canShowDropdown, loadPreferences]);

  const handleBellClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // On iOS outside the app the browser has no push at all: installing is
    // the way, so that is checked before "not supported".
    if (push.needsAppInstall) {
      setShowInstructionsDialog(true);
      return;
    }
    if (!push.isSupported) {
      toast({
        title: t('notifications.notSupported'),
        description: t('notifications.notSupportedDescription'),
        variant: 'destructive',
      });
      return;
    }
    if (permission === 'denied') {
      setShowUnblockSteps(true);
      return;
    }
    await subscribeAndEnableAll();
  };

  const subscribeAndEnableAll = async () => {
    try {
      await push.enableNotifications('gym_bell');
    } catch (error) {
      if (getNotificationPermission() === 'denied') {
        toast({
          title: t('notifications.permissionDenied'),
          description: t('notifications.permissionDeniedDescription'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('notifications.subscriptionFailed'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      }
      return;
    }

    setPreferencesLoading(true);
    try {
      await loadPreferences();
      toast({
        title: t('notifications.allEnabled'),
        description: t('notifications.allEnabledDescription_gym', {
          defaultValue: t('notifications.allEnabledDescription'),
        }),
      });
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleToggle = async (
    type: GymNotificationPreference['notification_type'],
    channel: 'push_enabled' | 'email_enabled',
    value: boolean
  ) => {
    const key = `${type}-${channel}`;
    setUpdatingKey(key);

    const updated = preferences.map((p) =>
      p.notification_type === type ? { ...p, [channel]: value } : p
    );
    setPreferences(updated);
    onSubscriptionChange?.(updated.some((p) => p.push_enabled));

    try {
      const pref = updated.find((p) => p.notification_type === type)!;
      await gymNotificationPreferencesService.updatePreferences(notifiableSlug, [pref]);
    } catch {
      setPreferences(preferences);
      onSubscriptionChange?.(preferences.some((p) => p.push_enabled));
      toast({ title: t('notifications.updateFailed'), variant: 'destructive' });
    } finally {
      setUpdatingKey(null);
    }
  };

  const renderInstructionContent = () => {
    if (push.needsAppInstall) {
      return (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              {t('notifications.iosInstallTitle', { defaultValue: 'Install the App' })}
            </DialogTitle>
            <DialogDescription>
              {t('notifications.iosInstallDescription', {
                defaultValue: 'To receive push notifications on iOS, install the app first.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>{t('notifications.iosStep1', { defaultValue: 'Tap the Share button in Safari' })}</li>
              <li>{t('notifications.iosStep2', { defaultValue: 'Tap "Add to Home Screen"' })}</li>
              <li>{t('notifications.iosStep3', { defaultValue: 'Tap "Add"' })}</li>
              <li>{t('notifications.iosStep4', { defaultValue: 'Open the app from your home screen' })}</li>
              <li>{t('notifications.iosStep5', { defaultValue: 'Enable notifications here' })}</li>
            </ol>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInstructionsDialog(false)}>
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </DialogFooter>
        </>
      );
    }

    return null;
  };

  if (!visible) return null;

  if (!canShowDropdown) {
    return (
      <>
        <Button
          variant="outline"
          size="icon"
          onClick={handleBellClick}
          disabled={disabled || loading}
          title={t('notifications.enableNotifications')}
          className={cn(triggerClassName)}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <BellOff className="h-4 w-4" />
          )}
        </Button>
        <Dialog open={showInstructionsDialog} onOpenChange={setShowInstructionsDialog}>
          <DialogContent>{renderInstructionContent()}</DialogContent>
        </Dialog>
        <PushUnblockSteps open={showUnblockSteps} onOpenChange={setShowUnblockSteps} />
      </>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild disabled={disabled || loading}>
        <Button
          variant="outline"
          size="icon"
          disabled={disabled || loading}
          title={hasAnyPushEnabled ? t('notifications.manageNotifications') : t('notifications.enableNotifications')}
          className={cn(triggerClassName)}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : hasAnyPushEnabled ? (
            <Bell className="h-4 w-4" />
          ) : (
            <BellOff className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 p-3 bg-popover border rounded-md shadow-lg">
        <div className="space-y-3">
          <p className="font-medium text-sm">{t('notifications.notificationSettings')}</p>

          {/* Column headers */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center text-xs text-muted-foreground pb-1 border-b">
            <span />
            <span className="flex items-center gap-1 w-12 justify-center">
              <Smartphone className="h-3 w-3" />
              {t('notificationSettings.push')}
            </span>
            <span className="flex items-center gap-1 w-12 justify-center">
              <Mail className="h-3 w-3" />
              {t('notificationSettings.email')}
            </span>
          </div>

          {/* Preference rows */}
          {GYM_TYPES.map((type) => {
            const pref = preferences.find((p) => p.notification_type === type);
            const pushEnabled = pref?.push_enabled ?? true;
            const emailEnabled = pref?.email_enabled ?? false;

            return (
              <div key={type} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                <span className="text-sm">
                  {t(`notificationSettings.types.${type}`)}
                </span>
                <div className="flex w-12 justify-center">
                  <Switch
                    checked={pushEnabled}
                    onCheckedChange={(v) => handleToggle(type, 'push_enabled', v)}
                    disabled={updatingKey === `${type}-push_enabled`}
                    aria-label={`${t(`notificationSettings.types.${type}`)} push`}
                  />
                </div>
                <div className="flex w-12 justify-center">
                  <Switch
                    checked={emailEnabled}
                    onCheckedChange={(v) => handleToggle(type, 'email_enabled', v)}
                    disabled={updatingKey === `${type}-email_enabled`}
                    aria-label={`${t(`notificationSettings.types.${type}`)} email`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
